/**
 * TokenSelector Component
 * Dropdown to select supported tokens with balance display
 */

import { useAccount, useReadContract, useBalance } from 'wagmi'
import { SUPPORTED_TOKENS, Token } from '@contracts/tokens'
import { ERC20ABI } from '@contracts/abis'
import { formatTokenAmount } from '@utils/format'

interface TokenSelectorProps {
  value?: Token | { symbol: 'BTC'; name: 'Bitcoin'; decimals: 8; isNative: true }
  onChange: (token: Token | { symbol: 'BTC'; name: 'Bitcoin'; decimals: 8; isNative: true }) => void
  showBalance?: boolean
  label?: string
  disabled?: boolean
  includeBTC?: boolean  // Show BTC as first option (for Squid Router bridge)
  tokens?: Token[]      // Optional: restrict to specific tokens (e.g., deposited tokens)
  vaultBalances?: Record<string, { available: bigint; reserved: bigint }> // Show vault balance instead of wallet
}

// BTC pseudo-token for Squid Router
export const BTC_TOKEN = {
  symbol: 'BTC' as const,
  name: 'Bitcoin',
  decimals: 8,
  isNative: true,
  address: '0x0000000000000000000000000000000000000000' as `0x${string}`,
  category: 'btc' as const,
}

export function TokenSelector({
  value,
  onChange,
  showBalance = true,
  label = 'Select Token',
  disabled = false,
  includeBTC = false,
  tokens,
  vaultBalances,
}: TokenSelectorProps) {
  const { address } = useAccount()

  // Use provided tokens or fall back to all supported tokens
  const availableTokens = tokens || Object.values(SUPPORTED_TOKENS)

  const handleChange = (symbol: string) => {
    if (symbol === 'BTC' && includeBTC) {
      onChange(BTC_TOKEN)
    } else {
      // Look in provided tokens first, then fallback to SUPPORTED_TOKENS
      const token = availableTokens.find(t => t.symbol === symbol) || SUPPORTED_TOKENS[symbol]
      if (token) {
        onChange(token)
      }
    }
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <select
        value={value?.symbol || ''}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
        className={`
          w-full px-4 py-3 rounded-lg border
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white dark:bg-gray-800'}
          border-gray-300 dark:border-gray-600
          focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange
          text-gray-900 dark:text-white
        `}
      >
        <option value="">Select a token...</option>
        {includeBTC && (
          <option value="BTC" className="font-semibold">
            ₿ Bitcoin (BTC) - Bridge via Squid Router
          </option>
        )}
        {availableTokens.map((token) => (
          <TokenOption
            key={token.symbol}
            token={token}
            userAddress={address}
            showBalance={showBalance}
            vaultBalance={vaultBalances?.[token.address.toLowerCase()]}
          />
        ))}
      </select>
    </div>
  )
}

/**
 * Individual token option with balance
 */
function TokenOption({
  token,
  userAddress,
  showBalance,
  vaultBalance,
}: {
  token: Token
  userAddress?: `0x${string}`
  showBalance: boolean
  vaultBalance?: { available: bigint; reserved: bigint }
}) {
  // If vault balance is provided, use it instead of wallet balance
  const useVaultBalance = !!vaultBalance
  
  // Get ETH balance for native tokens (wallet only)
  const { data: ethBalance } = useBalance({
    address: userAddress,
    query: {
      enabled: !!userAddress && showBalance && token.isNative && !useVaultBalance,
    },
  })
  
  // Get ERC20 balance for non-native tokens (wallet only)
  const { data: erc20Balance } = useReadContract({
    address: token.address,
    abi: ERC20ABI,
    functionName: 'balanceOf',
    args: userAddress ? [userAddress] : undefined,
    query: {
      enabled: !!userAddress && showBalance && !token.isNative && !useVaultBalance,
    },
  })

  // Use vault balance if provided, otherwise use wallet balance
  const actualBalance = useVaultBalance 
    ? vaultBalance.available
    : (token.isNative ? ethBalance?.value : (erc20Balance as bigint | undefined))
  
  const formattedBalance = actualBalance
    ? formatTokenAmount(actualBalance, token.decimals)
    : '0'
  
  const balanceLabel = useVaultBalance ? 'Vault' : 'Balance'

  return (
    <option value={token.symbol}>
      {token.symbol} - {token.name}
      {showBalance && userAddress && ` (${balanceLabel}: ${formattedBalance})`}
    </option>
  )
}

